import { useCallback, useMemo, useState, useEffect } from 'react';
import AdSection from './media/AdSection';

const IMAGE_TYPES = {
  google: [
    { key: 'MARKETING_IMAGE', label: 'Imágenes de Marketing' },
    { key: 'SQUARE_MARKETING_IMAGE', label: 'Imágenes cuadradas' },
    { key: 'LOGO', label: 'Logos' },
    { key: 'IMAGE_AD', label: 'Anuncios de Imagen' }
  ],
  meta: [
    { key: 'image', label: 'Imágenes' }
  ]
};

const NON_EDITABLE_TYPES = ['IMAGE_AD'];

const VALIDATION_RULES = {
  MARKETING_IMAGE: { min: 1, max: 15 },
  SQUARE_MARKETING_IMAGE: { min: 1, max: 15 },
  LOGO: { min: 1, max: 5 },
  image: { min: 1, max: 1 }
};

const WARNING_MESSAGES = {
  MIN_REQUIRED: (min, type) => `Debe haber al menos ${min} ${type.toLowerCase()}.`,
  MAX_EXCEEDED: (max, type) => `Debe haber como máximo ${max} ${type.toLowerCase()}.`,
  EXACT_ONE: (type) => `Debe haber exactamente 1 ${type.toLowerCase()}.`
};

export default function ImagesSection({ data, editMode, onDataChange, platform }) {
  const types = useMemo(() => IMAGE_TYPES[platform] || [], [platform]);
  const [previews, setPreviews] = useState({});
  const [warnings, setWarnings] = useState({});
  const [expanded, setExpanded] = useState(() => {
    const init = {};
    if (data.ad_content && data.ad_content.length) {
      init[data.ad_content[0].id] = true;
    }
    return init;
  });
  const [updates, setUpdates] = useState({});

  useEffect(() => {
    setPreviews({});
    setUpdates({});
    setWarnings({});
  }, [data.ad_content]);

  const getInitialTypesForAd = useCallback((ad) => {
    const imageUrls = ad.image_urls || [];
    const existingTypes = [...new Set(imageUrls.map(img => img.type))];
    return types.filter(typeDef => existingTypes.includes(typeDef.key));
  }, [types]);

  useEffect(() => {
    const newWarnings = {};

    data.ad_content.forEach(ad => {
      const adId = ad.id;
      const imgs = ad.image_urls || [];
  
      const { to_add = [], to_remove = [] } = updates[adId]?.assets?.images || {};
      const removedIds = to_remove.map(r => r.asset_group_resource_name || r.id);
  
      const adWarnings = {};
  
      getInitialTypesForAd(ad).forEach(({ key: type, label }) => {
        const original = imgs.filter(i =>
          i.type === type &&
          !removedIds.includes(i.asset_group_resource_name || i.id)
        );
        const added = to_add.filter(a => a.type === type);
        const total = original.length + added.length;
        const rules = VALIDATION_RULES[type];
  
        if (!rules) {
          return;
        }
  
        if (rules.min === rules.max && rules.min === 1) {
          if (total !== 1) {
            adWarnings[type] = WARNING_MESSAGES.EXACT_ONE(label);
          } else {
          }
        } else {
          if (total < rules.min) {
            adWarnings[type] = WARNING_MESSAGES.MIN_REQUIRED(rules.min, label);
          } else if (total > rules.max) {
            adWarnings[type] = WARNING_MESSAGES.MAX_EXCEEDED(rules.max, label);
          }
        }
      });
  
      newWarnings[adId] = adWarnings;
    });
  
    setWarnings(newWarnings);
  }, [data.ad_content, updates, getInitialTypesForAd]);  

  const notifyChange = (newUpdates, newPreviews) => {
    if (!editMode) return;
    const fileMap = Object.values(newPreviews)
      .flatMap(ad => Object.values(ad || {}))
      .flat()
      .reduce((map, preview) => {
        map[preview.file_placeholder] = preview.file;
        return map;
      }, {});
    onDataChange?.({ assetsUpdates: newUpdates, fileMap });
  };

  const handleToggle = adId => setExpanded(prev => ({ ...prev, [adId]: !prev[adId] }));

  const handleUpload = (adId, type, files) => {
    const list = Array.from(files).map((file, idx) => ({
      file,
      file_placeholder: `file_${Date.now()}_${adId}_${type}_${idx}`,
      url: URL.createObjectURL(file),
      type
    }));
    const newPreviews = {
      ...previews,
      [adId]: {
        ...(previews[adId] || {}),
        [type]: [ ...(previews[adId]?.[type] || []), ...list ]
      }
    };
    setPreviews(newPreviews);
    const prev = updates[adId]?.assets?.images || { to_add: [], to_remove: [] };
    const newUpdates = {
      ...updates,
      [adId]: { assets: { images: { to_add: [...prev.to_add, ...list.map(i => ({ type: i.type, file_placeholder: i.file_placeholder }))], to_remove: prev.to_remove } } }
    };
    setUpdates(newUpdates);
    notifyChange(newUpdates, newPreviews);
  };

  const handleRemove = ({ adId, type, id, isNew }) => {
    let newPreviews = previews;
    let newUpdates = updates;
    if (isNew) {
      newPreviews = {
        ...previews,
        [adId]: { ...(previews[adId] || {}), [type]: (previews[adId]?.[type] || []).filter(p => p.file_placeholder !== id) }
      };
      setPreviews(newPreviews);
      const prev = updates[adId]?.assets?.images || { to_add: [], to_remove: [] };
      newUpdates = {
        ...updates,
        [adId]: { assets: { images: { to_add: prev.to_add.filter(u => u.file_placeholder !== id), to_remove: prev.to_remove } } }
      };
    } else {
      const prev = updates[adId]?.assets?.images || { to_add: [], to_remove: [] };
      newUpdates = {
        ...updates,
        [adId]: { assets: { images: { to_add: prev.to_add, to_remove: [...prev.to_remove, { asset_group_resource_name: id }] } } }
      };
    }
    setUpdates(newUpdates);
    notifyChange(newUpdates, newPreviews);
  };

  const adsWithImages = data.ad_content.filter(ad =>
    getInitialTypesForAd(ad).length > 0
  );

  return (
    <div className="space-y-6 mt-6">
      <h3 className="text-sm font-medium text-gray-500 mb-3">Imágenes por anuncio</h3>
      <div className="space-y-6">
        {adsWithImages.map(ad => (
          <AdSection
            key={ad.id}
            ad={ad}
            types={getInitialTypesForAd(ad)}
            updates={updates}
            previews={previews}
            warnings={warnings}
            expanded={expanded[ad.id] || false}
            onToggle={handleToggle}
            onUpload={handleUpload}
            onRemove={handleRemove}
            editMode={editMode && !NON_EDITABLE_TYPES.includes(ad.type)}
          />
        ))}
      </div>
    </div>
  );
}
